import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/app/session';
import { useNavBadges } from '@/app/badges';
import { chromeFor } from '@/app/chrome';
import { BINDINGS, GO_TO } from '@/app/shortcuts';
import { getFactsClock, subscribeFactsClock } from '@/app/changes';
import { ageStateFor, relativeAge } from '@/lib/format';
import { isTypingTarget, useIsPhone, useLocalPreference, useNow } from '@/lib/hooks';
import { api } from '@/lib/api';
import { navSectionsFor, tabsFor } from './nav';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { BottomTabs } from './BottomTabs';
import { CommandPalette } from './CommandPalette';
import { Drawer } from './Drawer';
import { Button, Pill } from './Primitives';
import { MockRoleSwitch } from './MockRoleSwitch';

export interface AppShellProps {
  children: ReactNode;
}

/**
 * Sidebar plus top bar plus content on desktop, bottom tabs on a phone. It owns the command
 * palette, the global keyboard map and the global data-age chip.
 *
 * The lock screen is A2's: this shell calls the auth adapter's lock, and A2's route takes
 * over above everything at z-index lock-screen.
 */
export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const isPhone = useIsPhone();
  const now = useNow(5000);
  const { role, permissions } = useSession();
  const badges = useNavBadges();

  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const chrome = useMemo(() => chromeFor(pathname, permissions), [pathname, permissions]);

  const [collapsed, setCollapsed] = useLocalPreference('ops.sidebar.collapsed', false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const clock = useSyncExternalStore(subscribeFactsClock, getFactsClock, getFactsClock);
  const ageState = ageStateFor(clock.lastSuccessAt, 'live', now);
  const ageText = clock.lastSuccessAt ? relativeAge(clock.lastSuccessAt, now) : 'connecting';

  const sections = useMemo(
    () => navSectionsFor(role, badges, permissions),
    [badges, permissions, role],
  );
  const tabs = useMemo(() => tabsFor(role), [role]);

  const refresh = useCallback(() => {
    void client.invalidateQueries();
  }, [client]);

  const lock = useCallback(() => {
    void api.post('/auth/lock').catch(() => undefined);
    void navigate({ to: '/lock' });
  }, [navigate]);

  // Global keyboard map. Single keys are ignored while the operator is typing.
  useEffect(() => {
    let awaitingGo = false;
    let goTimer = 0;

    const onKeyDown = (event: KeyboardEvent) => {
      const isPaletteKey = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k';
      if (isPaletteKey) {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (isTypingTarget(event.target) || event.ctrlKey || event.metaKey || event.altKey) return;

      if (awaitingGo) {
        awaitingGo = false;
        window.clearTimeout(goTimer);
        const target = GO_TO[event.key.toLowerCase()];
        if (target) {
          event.preventDefault();
          void navigate({ to: target });
        }
        return;
      }

      if (event.key === 'g') {
        awaitingGo = true;
        goTimer = window.setTimeout(() => {
          awaitingGo = false;
        }, 1200);
        return;
      }
      if (event.key === 'i') {
        event.preventDefault();
        void navigate({ to: '/inbox' });
        return;
      }
      if (event.key === '?') {
        event.preventDefault();
        setShortcutsOpen(true);
        return;
      }
      if (event.key === 'Escape') {
        setMoreOpen(false);
        setShortcutsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(goTimer);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [navigate]);

  return (
    <div className="o-shell">
      {!isPhone ? (
        <Sidebar
          sections={sections}
          collapsed={collapsed}
          onToggle={() => setCollapsed(!collapsed)}
          role={role}
          connected={ageState === 'fresh' || ageState === 'stale'}
          onLock={lock}
          footer={<MockRoleSwitch />}
        />
      ) : null}

      <div className="o-shell__main">
        <TopBar
          title={chrome.title}
          segments={chrome.segments}
          {...(chrome.activeSegment ? { activeSegment: chrome.activeSegment } : {})}
          onSearch={() => setPaletteOpen(true)}
          ageState={ageState}
          ageText={ageText}
          onRefresh={refresh}
          inboxCount={badges.inboxCount}
        />
        <main className="o-shell__content" id="main">
          {children}
        </main>
      </div>

      {isPhone ? (
        <BottomTabs
          tabs={tabs}
          onSearch={() => setPaletteOpen(true)}
          onMore={() => setMoreOpen((current) => !current)}
          moreOpen={moreOpen}
        />
      ) : null}

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      <Drawer open={moreOpen} onClose={() => setMoreOpen(false)} title="More" id="more-sheet">
        <div className="o-stack">
          {sections
            .filter((section) => !tabs.some((tab) => tab.href === section.href))
            .map((section) => (
              <Link
                key={section.id}
                to={section.href}
                className="o-chip"
                onClick={() => setMoreOpen(false)}
              >
                {section.label}
              </Link>
            ))}
          <div className="o-row">
            <Pill tone="copper">{role ?? 'signed out'}</Pill>
            <Button variant="ghost" onClick={lock}>
              Lock
            </Button>
          </div>
        </div>
      </Drawer>

      <Drawer
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
        title="Keyboard shortcuts"
        id="shortcuts"
      >
        <div className="o-stack">
          {['Global', 'Lists', 'Order drawer', 'Stock'].map((scope) => (
            <section key={scope} className="o-stack">
              <h3 className="o-section-title">{scope}</h3>
              {BINDINGS.filter((binding) => binding.scope === scope).map((binding) => (
                <div className="o-kv" key={`${scope}-${binding.keys}-${binding.action}`}>
                  <span className="o-kv__key">{binding.action}</span>
                  <span className="o-kv__value">{binding.keys}</span>
                </div>
              ))}
            </section>
          ))}
        </div>
      </Drawer>
    </div>
  );
}
